import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuMapPin, LuShieldCheck, LuPlus, LuPencil, LuTrash2 } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import { VisitaEquipo } from './InformeTrabajo';
import { linkMaps } from '../../utils/pedidosEmpresa';
import { estadoGarantia } from '../../utils/dateUtils';

// Ficha permanente de un equipo (Portal Empresa, etapa 2): todas las visitas
// con lo que se hizo, repuestos y fotos. Desde acá se pide servicio directo.
// 10-oct-2026: los equipos son de la empresa, así que los puede corregir (N/S,
// modelo, ubicación, lugar) y dar de baja. La baja no borra el historial.
const fmt = (iso) => {
    if (!iso) return '';
    const [a, m, d] = String(iso).slice(0, 10).split('-');
    return `${d}/${m}/${a}`;
};

export default function FichaEquipoSheet({ serie, id, onCerrar, onPedirServicio, onCambio }) {
    const [eq, setEq] = useState(null);
    const [error, setError] = useState(false);
    const [accion, setAccion] = useState(null); // null | 'editar' | 'baja'

    useEffect(() => {
        api.get('/empresa/equipo', { params: id != null ? { id } : { serie } }).then(r => setEq(r.data)).catch(() => setError(true));
    }, [serie, id]);

    return (
        <ModalShell titulo={`Equipo N/S ${eq?.serie || serie}`} subtitulo={eq ? [eq.marca, eq.modelo, eq.ubicacion].filter(Boolean).join(' · ') || 'Historial del equipo' : 'Historial del equipo'}
            onCerrar={onCerrar} ancho="md:max-w-xl"
            pie={eq && !accion ? <button type="button" onClick={() => onPedirServicio && onPedirServicio(eq)}
                className="w-full h-12 rounded-xl bg-[#C9341F] text-white font-black text-body inline-flex items-center justify-center gap-1.5 active:scale-95"><LuPlus size={18} /> Pedir servicio para este equipo</button> : null}>
            {error ? <p className="py-10 text-center text-muted">No se encontró el equipo.</p> : !eq ? <p className="py-10 text-center text-muted">Cargando…</p> : (
                <div className="space-y-4">
                    {accion === 'editar' ? (
                        <EditarEquipo eq={eq} onCancelar={() => setAccion(null)}
                            onGuardado={(nuevo) => { setAccion(null); setEq(x => ({ ...x, ...nuevo })); onCambio && onCambio(nuevo); }} />
                    ) : accion === 'baja' ? (
                        <BajaEquipo eq={eq} onCancelar={() => setAccion(null)} onListo={() => { onCambio && onCambio(null); onCerrar(); }} />
                    ) : eq.id != null && (
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setAccion('editar')}
                                className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95"><LuPencil size={14} /> Editar datos</button>
                            <button type="button" onClick={() => setAccion('baja')}
                                className="flex-1 h-10 rounded-xl bg-chip text-secondary text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95"><LuTrash2 size={14} /> Dar de baja</button>
                        </div>
                    )}
                    <a href={linkMaps(eq.direccion)} target="_blank" rel="noreferrer" className="flex items-start gap-2.5 p-3 rounded-2xl bg-chip">
                        <LuMapPin size={17} className="shrink-0 mt-0.5 text-brand-red" />
                        <span className="flex-1 min-w-0">
                            {eq.sede && <span className="block text-body font-black text-ink">{eq.sede}</span>}
                            <span className="block text-label text-secondary">{eq.direccion}</span>
                        </span>
                    </a>
                    {eq.garantiaHasta && estadoGarantia(eq.garantiaHasta)?.vigente && (
                        <p className="flex items-center gap-2 p-3 rounded-2xl bg-[rgba(22,163,74,0.1)] text-body font-black text-[#16A34A]"><LuShieldCheck size={18} /> En garantía hasta el {fmt(eq.garantiaHasta)}</p>
                    )}
                    <div className="space-y-2">
                        <p className="text-label font-black uppercase tracking-widest text-muted">Historial · {eq.visitas?.length || 0} visita{eq.visitas?.length === 1 ? '' : 's'}</p>
                        {(!eq.visitas || eq.visitas.length === 0) && <p className="text-body text-muted">Todavía no tiene visitas registradas.</p>}
                        {eq.visitas?.map((v, i) => <VisitaEquipo key={i} v={v} mostrarSerie={false} />)}
                    </div>
                </div>
            )}
        </ModalShell>
    );
}

const CAMPO = 'w-full h-11 px-3.5 rounded-xl bg-chip text-body text-ink outline-none placeholder:text-muted';

function EditarEquipo({ eq, onCancelar, onGuardado }) {
    const [f, setF] = useState({ numeroSerie: eq.serie || '', modelo: eq.modelo || '', ubicacion: eq.ubicacion || '', sedeId: eq.sedeId });
    const [lugares, setLugares] = useState([]);
    const [guardando, setGuardando] = useState(false);
    useEffect(() => { api.get('/empresa/sedes').then(r => setLugares(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
    const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
    const guardar = async () => {
        if (!f.numeroSerie.trim()) { toast.error('Poné el N/S del equipo'); return; }
        setGuardando(true);
        try {
            const r = await api.put(`/empresa/equipos/${eq.id}`, { ...f, sedeId: Number(f.sedeId) || null });
            toast.success('Equipo actualizado');
            onGuardado(r.data);
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo guardar'); }
        finally { setGuardando(false); }
    };
    return (
        <div className="p-4 rounded-2xl border border-black/[0.08] dark:border-white/[0.08] space-y-2.5">
            <p className="text-label font-black uppercase tracking-widest text-muted">Editar equipo</p>
            <label className="block space-y-1"><span className="text-caption font-black text-secondary">N/S</span>
                <input value={f.numeroSerie} onChange={set('numeroSerie')} className={CAMPO} placeholder="Número de serie" /></label>
            <label className="block space-y-1"><span className="text-caption font-black text-secondary">Modelo</span>
                <input value={f.modelo} onChange={set('modelo')} className={CAMPO} placeholder="Opcional" /></label>
            <label className="block space-y-1"><span className="text-caption font-black text-secondary">Ubicación</span>
                <input value={f.ubicacion} onChange={set('ubicacion')} className={CAMPO} placeholder="Ej.: cocina, 2° piso" /></label>
            {lugares.length > 1 && (
                <label className="block space-y-1"><span className="text-caption font-black text-secondary">Lugar</span>
                    <select value={f.sedeId || ''} onChange={set('sedeId')} className={CAMPO}>
                        {lugares.map(l => <option key={l.id} value={l.id}>{l.nombre}{l.direccion ? ` · ${l.direccion}` : ''}</option>)}
                    </select></label>
            )}
            <div className="flex gap-2 pt-1">
                <button type="button" onClick={onCancelar} disabled={guardando}
                    className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cancelar</button>
                <button type="button" onClick={guardar} disabled={guardando}
                    className="flex-[2] h-11 rounded-xl bg-[#C9341F] text-white text-label font-black active:scale-95 disabled:opacity-50">Guardar cambios</button>
            </div>
        </div>
    );
}

function BajaEquipo({ eq, onCancelar, onListo }) {
    const [enviando, setEnviando] = useState(false);
    const confirmar = async () => {
        setEnviando(true);
        try {
            await api.delete(`/empresa/equipos/${eq.id}`);
            toast.success('Equipo dado de baja');
            onListo();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo dar de baja'); setEnviando(false); }
    };
    return (
        <div className="p-4 rounded-2xl bg-[rgba(201,52,31,0.08)] space-y-2.5">
            <p className="text-body font-black text-ink">¿Dar de baja el N/S {eq.serie}?</p>
            <p className="text-caption text-secondary">Sale de tu lista de equipos. Las visitas que ya tuvo quedan guardadas, y si fue un error nos escribís y lo recuperamos.</p>
            <div className="flex gap-2">
                <button type="button" onClick={onCancelar} disabled={enviando}
                    className="flex-1 h-11 rounded-xl bg-chip text-secondary text-label font-black active:scale-95">Cancelar</button>
                <button type="button" onClick={confirmar} disabled={enviando}
                    className="flex-[2] h-11 rounded-xl bg-[#C9341F] text-white text-label font-black active:scale-95 disabled:opacity-50">Sí, dar de baja</button>
            </div>
        </div>
    );
}
